'use client';

import { Badge, Box, Button, Flex, HStack, IconButton, Text } from '@chakra-ui/react';
import { createPreview, type Preview } from '@codesandbox/sdk/browser';
import {
  CheckCircle2,
  CircleX,
  Clock3,
  Crosshair,
  ExternalLink,
  FileDiff,
  Monitor,
  PanelRightOpen,
  RefreshCw,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import { useAuth } from '@/app/auth-provider';
import { PREVIEW_INSPECTOR_COLOR } from '@/components/preview/inspector-style';
import { PreviewInspectorButton } from '@/components/preview/PreviewInspectorButton';
import { usePreviewAnnotation } from '@/components/preview/usePreviewAnnotation';
import { useAgentPreviewCapture } from '@/components/preview/useAgentPreviewCapture';
import { PreviewAnnotationToolbar } from '@/components/preview/PreviewAnnotationToolbar';
import { PreviewAnnotationOverlay } from '@/components/preview/PreviewAnnotationOverlay';
import {
  installAttunePreviewAnnotationBridge,
  type PreviewAnnotationCaptureRequest,
  type PreviewAnnotationCancelRequest,
  type PreviewAnnotationCaptureResult,
} from '@/components/preview/annotation-bridge';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { Tooltip } from '@/components/ui/tooltip';
import { subscribeDeliveryApprovalsChanged } from '@/lib/delivery-approvals/client-events';
import { isGitHubLivePreviewEnabledClient } from '@/lib/github/feature-flags';
import {
  parseGitHubPreviewBrowserContextInput,
  sanitizeGitHubPreviewPath,
  type GitHubPreviewBrowserContextInput,
  type GitHubPreviewBrowserContextSnapshot,
} from '@/lib/github/preview/browser-context-shared';
import {
  GitHubPreviewChangesPanel,
  type GitHubPreviewChangesResponse,
} from './GitHubPreviewChangesPanel';
import { createPreviewBridgeLifecycle } from './preview-bridge-lifecycle';

export interface WebsitePreviewMetadata {
  id: string;
  status: string;
  revision: number;
  validatedRevision?: number | null;
  validationStatus?: string | null;
  previewPort: number;
  path: string;
  initialPath?: string | null;
  repository: string;
  nextAttemptAt?: string | null;
  queueReason?: 'provider_capacity' | 'provider_rate_limit' | 'transient_failure' | null;
  browserClientId?: string | null;
  browserLeaseToken?: string | null;
  error?: string | null;
}

type AttunePreviewMessageToFrame =
  | PreviewAnnotationCaptureRequest
  | PreviewAnnotationCancelRequest
  | { type: 'ATTUNE_INSPECTOR_SET'; enabled: boolean }
  | { type: 'ATTUNE_SELECTION_CLEAR' }
  | { type: 'ATTUNE_CONTEXT_CAPTURE' };

type AttunePreviewMessageFromFrame =
  | PreviewAnnotationCaptureResult
  | {
      type: 'ATTUNE_PREVIEW_CONTEXT';
      context: Omit<GitHubPreviewBrowserContextInput, 'contextVersion'>;
    }
  | { type: 'ATTUNE_INSPECTOR_STATE'; enabled: boolean };

const BROWSER_CONTEXT_HEARTBEAT_MS = 15_000;

function installAttunePreviewContextBridge({
  previewWindow,
  previewProtocol,
  scope,
}: {
  previewWindow: Window;
  previewProtocol: {
    addListener: <Type extends AttunePreviewMessageToFrame['type']>(
      type: Type,
      listener: (message: Extract<AttunePreviewMessageToFrame, { type: Type }>) => void
    ) => void;
    sendMessage: (message: AttunePreviewMessageFromFrame) => void;
  };
  scope: { inspectorEnabled: boolean; selectionColor: string };
}) {
  const bridgeWindow = previewWindow as Window & {
    __attunePreviewBridgeCleanup?: () => void;
  };
  bridgeWindow.__attunePreviewBridgeCleanup?.();

  const document = previewWindow.document;
  const selectionColor = scope.selectionColor;
  let active = true;
  let inspectorEnabled = Boolean(scope.inspectorEnabled);
  let selectedElement: Element | null = null;
  let hoveredElement: Element | null = null;
  let timer: number | undefined;
  const observer = new previewWindow.MutationObserver(() => scheduleCapture(400));

  const cleanText = (value: unknown, maxLength = 240) =>
    typeof value === 'string'
      ? value
          .replace(/[\u0000-\u001f\u007f]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, maxLength)
      : '';
  const elementText = (element: Element, maxLength = 240) => {
    if (/^(input|select|textarea)$/i.test(element.tagName)) return '';
    return cleanText(element.textContent, maxLength);
  };
  const isVisible = (element: Element) => {
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < previewWindow.innerHeight &&
      rect.left < previewWindow.innerWidth
    );
  };
  const elementRole = (element: Element) => cleanText(element.getAttribute('role'), 80) || null;
  const elementLabel = (element: Element) =>
    cleanText(
      element.getAttribute('aria-label') ||
        element.getAttribute('alt') ||
        element.getAttribute('title'),
      160
    ) || null;
  const safeReference = (value: string) => {
    try {
      const url = new URL(value, previewWindow.location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (/(auth|code|credential|key|secret|session|state|token)/i.test(key)) {
          url.searchParams.delete(key);
        }
      }
      return url.origin === previewWindow.location.origin
        ? `${url.pathname}${url.search}${url.hash}`.slice(0, 300)
        : `${url.origin}${url.pathname}`.slice(0, 300);
    } catch {
      return '';
    }
  };
  const selectorFor = (element: Element) => {
    const id = cleanText(element.getAttribute('id'), 120);
    if (id) return `#${previewWindow.CSS.escape(id)}`;
    const parts: string[] = [];
    let current: Element | null = element;
    while (current && current !== document.documentElement && parts.length < 6) {
      const tag = current.tagName.toLowerCase();
      const parent: Element | null = current.parentElement;
      if (!parent) {
        parts.unshift(tag);
        break;
      }
      const siblings = [...parent.children].filter(child => child.tagName === current!.tagName);
      const position = siblings.indexOf(current) + 1;
      parts.unshift(siblings.length > 1 ? `${tag}:nth-of-type(${position})` : tag);
      current = parent;
    }
    return parts.join(' > ').slice(0, 500) || element.tagName.toLowerCase();
  };
  const landmarkFor = (element: Element | null) => {
    if (!element) return null;
    const landmark = element.closest(
      'main,section,article,header,footer,nav,aside,[role="main"],[role="region"],[role="banner"],[role="navigation"],[role="contentinfo"],[aria-label]'
    );
    if (!landmark) return null;
    return {
      tag: landmark.tagName.toLowerCase(),
      role: elementRole(landmark),
      label: elementLabel(landmark),
      text: elementText(landmark, 240) || null,
    };
  };
  const selectedContext = () => {
    if (!selectedElement?.isConnected) selectedElement = null;
    if (!selectedElement) return null;
    const rect = selectedElement.getBoundingClientRect();
    const attributes: Record<string, string> = {};
    for (const name of ['id', 'class', 'role', 'aria-label', 'alt', 'href', 'src']) {
      const rawValue = selectedElement.getAttribute(name);
      if (!rawValue) continue;
      const value =
        name === 'href' || name === 'src' ? safeReference(rawValue) : cleanText(rawValue, 300);
      if (value) attributes[name] = value;
    }
    return {
      tag: selectedElement.tagName.toLowerCase(),
      role: elementRole(selectedElement),
      label: elementLabel(selectedElement),
      text: elementText(selectedElement) || null,
      selector: selectorFor(selectedElement),
      attributes,
      rect: {
        x: rect.x,
        y: rect.y + previewWindow.scrollY,
        width: rect.width,
        height: rect.height,
      },
      nearestSection: landmarkFor(selectedElement),
    };
  };
  const capture = () => {
    timer = undefined;
    if (!active) return;
    const visibleHeadings = [...document.querySelectorAll('h1,h2,h3,h4,h5,h6')]
      .filter(isVisible)
      .slice(0, 12)
      .flatMap(element => {
        const text = elementText(element, 160);
        return text ? [{ level: Number(element.tagName.slice(1)), text }] : [];
      });
    const visibleLandmarks = [
      ...document.querySelectorAll(
        'main,section,article,header,footer,nav,aside,[role="main"],[role="region"],[role="banner"],[role="navigation"],[role="contentinfo"]'
      ),
    ]
      .filter(isVisible)
      .slice(0, 12)
      .map(element => ({
        tag: element.tagName.toLowerCase(),
        role: elementRole(element),
        label: elementLabel(element),
        text: elementText(element, 240) || null,
      }));
    previewProtocol.sendMessage({
      type: 'ATTUNE_PREVIEW_CONTEXT',
      context: {
        path: previewWindow.location.href,
        title: cleanText(document.title, 200),
        viewport: {
          width: previewWindow.innerWidth,
          height: previewWindow.innerHeight,
          scrollX: Math.max(0, previewWindow.scrollX),
          scrollY: Math.max(0, previewWindow.scrollY),
          visibleHeadings,
          visibleLandmarks,
        },
        selection: selectedContext(),
      },
    });
  };
  function scheduleCapture(delay = 250) {
    if (!active) return;
    if (timer !== undefined) previewWindow.clearTimeout(timer);
    timer = previewWindow.setTimeout(capture, delay);
  }
  const clearHover = () => {
    hoveredElement?.removeAttribute('data-attune-preview-hover');
    hoveredElement = null;
  };
  const clearSelection = () => {
    if (!active) return;
    selectedElement?.removeAttribute('data-attune-preview-selected');
    selectedElement = null;
    scheduleCapture(0);
  };
  const setInspectorEnabled = (enabled: boolean) => {
    if (!active) return;
    inspectorEnabled = enabled;
    if (!enabled) clearHover();
    previewProtocol.sendMessage({ type: 'ATTUNE_INSPECTOR_STATE', enabled });
  };
  const onPointerOver = (event: Event) => {
    if (!inspectorEnabled || !event.target || (event.target as Node).nodeType !== 1) return;
    clearHover();
    hoveredElement = event.target as Element;
    hoveredElement.setAttribute('data-attune-preview-hover', 'true');
  };
  const onPointerOut = (event: Event) => {
    if (!inspectorEnabled || event.target !== hoveredElement) return;
    clearHover();
  };
  const onClick = (event: Event) => {
    if (!inspectorEnabled || !event.target || (event.target as Node).nodeType !== 1) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clearHover();
    selectedElement?.removeAttribute('data-attune-preview-selected');
    selectedElement = event.target as Element;
    selectedElement.setAttribute('data-attune-preview-selected', 'true');
    setInspectorEnabled(false);
    scheduleCapture(0);
  };
  const onScroll = () => scheduleCapture();
  const onResize = () => scheduleCapture();
  const onNavigation = () => {
    clearSelection();
  };

  const style = document.createElement('style');
  style.dataset.attunePreviewInspector = 'true';
  style.textContent = `
    [data-attune-preview-hover="true"] { outline: 2px dashed ${selectionColor} !important; outline-offset: 2px !important; cursor: crosshair !important; }
    [data-attune-preview-selected="true"] { outline: 3px solid ${selectionColor} !important; outline-offset: 2px !important; }
  `;
  document.documentElement.appendChild(style);
  document.addEventListener('pointerover', onPointerOver, true);
  document.addEventListener('pointerout', onPointerOut, true);
  document.addEventListener('click', onClick, true);
  previewWindow.addEventListener('scroll', onScroll, { passive: true });
  previewWindow.addEventListener('resize', onResize);
  previewWindow.addEventListener('popstate', onNavigation);
  previewWindow.addEventListener('hashchange', onNavigation);
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: false });
  previewProtocol.addListener('ATTUNE_INSPECTOR_SET', message => {
    if (active) setInspectorEnabled(Boolean(message.enabled));
  });
  previewProtocol.addListener('ATTUNE_SELECTION_CLEAR', () => {
    if (active) clearSelection();
  });
  previewProtocol.addListener('ATTUNE_CONTEXT_CAPTURE', () => {
    if (active) scheduleCapture(0);
  });
  bridgeWindow.__attunePreviewBridgeCleanup = () => {
    active = false;
    if (timer !== undefined) previewWindow.clearTimeout(timer);
    observer.disconnect();
    clearHover();
    selectedElement?.removeAttribute('data-attune-preview-selected');
    style.remove();
    document.removeEventListener('pointerover', onPointerOver, true);
    document.removeEventListener('pointerout', onPointerOut, true);
    document.removeEventListener('click', onClick, true);
    previewWindow.removeEventListener('scroll', onScroll);
    previewWindow.removeEventListener('resize', onResize);
    previewWindow.removeEventListener('popstate', onNavigation);
    previewWindow.removeEventListener('hashchange', onNavigation);
  };
  setInspectorEnabled(inspectorEnabled);
  scheduleCapture(0);
}

export function websitePreviewHref(preview: WebsitePreviewMetadata): string {
  return preview.initialPath
    ? `${preview.path}?path=${encodeURIComponent(preview.initialPath)}`
    : preview.path;
}

function previewLoadingContent(status: string) {
  if (status === 'queued') {
    return {
      title: 'Preview queued',
      detail: 'Attune is waiting for private preview capacity.',
      estimate: 'It will start automatically when capacity is available.',
    };
  }
  if (status === 'provisioning') {
    return {
      title: 'Preparing your private preview',
      detail: 'We’re creating a secure workspace for this site.',
      estimate: 'First launch usually takes 1–3 minutes.',
    };
  }
  if (status === 'installing') {
    return {
      title: 'Installing site dependencies',
      detail: 'The private workspace is setting up everything this site needs to run.',
      estimate: 'This step usually takes 30–90 seconds.',
    };
  }
  if (status === 'hibernated') {
    return {
      title: 'Waking your saved preview',
      detail: 'Your private workspace was paused while idle. Your work is still here.',
      estimate: 'Waking it usually takes 10–30 seconds.',
    };
  }
  if (status === 'validating') {
    return {
      title: 'Checking the latest revision',
      detail: 'We’re building your changes before showing the updated site.',
      estimate: 'Most builds finish in under a minute.',
    };
  }
  return {
    title: 'Opening your private preview',
    detail: 'The site is ready and the secure browser session is connecting.',
    estimate: 'This usually takes a few seconds.',
  };
}

function PreviewLoadingState({ status, variant }: { status: string; variant: 'inline' | 'rail' }) {
  const content = previewLoadingContent(status);
  return (
    <Flex
      role="status"
      aria-live="polite"
      flex={variant === 'rail' ? '1' : undefined}
      minH={variant === 'rail' ? '280px' : '210px'}
      align="center"
      justify="center"
      px={{ base: '4', md: '6' }}
      py="8"
      bg="ui.surface.subtle"
    >
      <Box
        w="full"
        maxW="440px"
        p={{ base: '4', md: '5' }}
        borderWidth="1px"
        borderColor="ui.border.muted"
        borderRadius="2xl"
        bg="ui.surface"
      >
        <HStack align="flex-start" gap="3">
          <Flex
            boxSize="10"
            flexShrink={0}
            align="center"
            justify="center"
            borderRadius="xl"
            bg="ui.surface.selected"
          >
            <Spinner size="sm" borderWidth="2px" color="ui.text.primary" />
          </Flex>
          <Box minW={0} flex="1">
            <HStack justify="space-between" align="flex-start" gap="2">
              <Text fontSize="sm" fontWeight="semibold" color="ui.text.primary">
                {content.title}
              </Text>
              <Badge size="sm" colorPalette="blue" variant="subtle" flexShrink={0}>
                In progress
              </Badge>
            </HStack>
            <Text mt="1" fontSize="sm" lineHeight="1.5" color="ui.text.secondary">
              {content.detail}
            </Text>
          </Box>
        </HStack>
        <HStack
          mt="4"
          px="3"
          py="2.5"
          gap="2"
          borderWidth="1px"
          borderColor="ui.border.subtle"
          borderRadius="lg"
          bg="ui.surface.subtle"
        >
          <Clock3 size={15} aria-hidden="true" />
          <Text fontSize="xs" fontWeight="medium" color="ui.text.secondary">
            {content.estimate}
          </Text>
        </HStack>
        <Text mt="3" fontSize="xs" lineHeight="1.5" color="ui.text.tertiary">
          No action needed—this updates automatically. If startup fails, the error will appear here.
        </Text>
      </Box>
    </Flex>
  );
}

export function GitHubPreviewLauncher({
  preview,
  onOpen,
}: {
  preview: WebsitePreviewMetadata;
  onOpen: (preview: WebsitePreviewMetadata) => void;
}) {
  const validationTone =
    preview.validationStatus === 'passed'
      ? 'green'
      : preview.validationStatus === 'failed' || preview.status === 'failed'
        ? 'red'
        : 'gray';
  return (
    <Flex
      mt="4"
      px="3"
      py="2.5"
      align="center"
      justify="space-between"
      gap="3"
      minW={0}
      borderWidth="1px"
      borderColor="ui.border.muted"
      borderRadius="xl"
      bg="ui.surface.subtle"
      containerType="inline-size"
      containerName="preview-launcher"
      css={{
        '@container preview-launcher (max-width: 30rem)': {
          '& [data-preview-action]': {
            width: '32px',
            minWidth: '32px',
            maxWidth: '32px',
            paddingInline: '0',
            gap: '0',
          },
          '& [data-preview-action-label]': {
            display: 'none',
          },
        },
      }}
    >
      <HStack gap="2" minW={0} flex="1">
        <Box as="span" display="inline-flex" flexShrink={0}>
          <Monitor size={16} aria-hidden="true" />
        </Box>
        <Box minW={0} flex="1">
          <Text fontSize="sm" fontWeight="semibold" truncate>
            {preview.repository}
          </Text>
          <Badge
            colorPalette={validationTone}
            size="sm"
            mt="1"
            maxW="full"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            Revision {preview.revision} · {preview.validationStatus || preview.status}
          </Badge>
        </Box>
      </HStack>
      <HStack gap="1" flexShrink={0}>
        <Button
          data-preview-action
          size="xs"
          variant="solid"
          onClick={() => onOpen(preview)}
          aria-label="Open website preview"
          title="Open preview"
        >
          <PanelRightOpen size={14} aria-hidden="true" />
          <Text as="span" data-preview-action-label>
            Open preview
          </Text>
        </Button>
        <Button data-preview-action size="xs" variant="ghost" asChild>
          <a
            href={websitePreviewHref(preview)}
            target="_blank"
            rel="noreferrer"
            aria-label="Open website preview in a new tab"
            title="Open in new tab"
          >
            <ExternalLink size={14} aria-hidden="true" />
            <Text as="span" data-preview-action-label>
              New tab
            </Text>
          </a>
        </Button>
      </HStack>
    </Flex>
  );
}

function previewInitialPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  return path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('\\') &&
    path.length <= 2048 &&
    !/[\u0000-\u001f\u007f]/.test(path)
    ? path
    : null;
}

export function websitePreviewFromToolMetadata(
  metadata: Record<string, unknown> | undefined
): WebsitePreviewMetadata | null {
  if (!metadata) return null;
  const nested =
    metadata.websitePreview && typeof metadata.websitePreview === 'object'
      ? (metadata.websitePreview as Record<string, unknown>)
      : null;
  if (!nested || typeof nested.id !== 'string' || !nested.id.trim()) return null;
  return {
    id: nested.id.trim(),
    status: typeof nested.status === 'string' ? nested.status : 'ready',
    revision: typeof nested.revision === 'number' ? nested.revision : 0,
    validatedRevision:
      typeof nested.validatedRevision === 'number' ? nested.validatedRevision : null,
    validationStatus: typeof nested.validationStatus === 'string' ? nested.validationStatus : null,
    previewPort: typeof nested.previewPort === 'number' ? nested.previewPort : 4321,
    path: typeof nested.path === 'string' ? nested.path : `/app/previews/${nested.id.trim()}`,
    initialPath: previewInitialPath(nested.initialPath),
    repository: typeof nested.repository === 'string' ? nested.repository : 'Astro site',
    error: typeof nested.error === 'string' ? nested.error : null,
  };
}

export function GitHubLivePreview({
  preview,
  variant = 'inline',
  onClose,
  onPreviewChange,
  onBrowserContextChange,
  onSelectionClearHandlerChange,
}: {
  preview: WebsitePreviewMetadata;
  variant?: 'inline' | 'rail';
  onClose?: () => void;
  onPreviewChange?: (preview: WebsitePreviewMetadata) => void;
  onBrowserContextChange?: (context: GitHubPreviewBrowserContextSnapshot | null) => void;
  onSelectionClearHandlerChange?: (handler: (() => void) | null) => void;
}) {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [receipt, setReceipt] = useState(preview);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [inspectorEnabled, setInspectorEnabled] = useState(false);
  const [inspectorReady, setInspectorReady] = useState(false);
  const [inspectorError, setInspectorError] = useState<string | null>(null);
  const [browserContext, setBrowserContext] = useState<GitHubPreviewBrowserContextSnapshot | null>(
    null
  );
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewBuildStarting, setReviewBuildStarting] = useState(false);
  const [reviewBuildError, setReviewBuildError] = useState<string | null>(null);
  const [changesSummary, setChangesSummary] = useState<GitHubPreviewChangesResponse | null>(null);
  const [changesError, setChangesError] = useState<string | null>(null);
  const browserContextPathRef = useRef<string | null>(null);
  browserContextPathRef.current = browserContext?.path || null;
  const inspectorEnabledRef = useRef(inspectorEnabled);
  inspectorEnabledRef.current = inspectorEnabled;
  const previewApiRef = useRef<Preview<
    AttunePreviewMessageToFrame,
    AttunePreviewMessageFromFrame
  > | null>(null);
  const contextVersionRef = useRef(0);
  const contextSignatureRef = useRef('');
  const contextPersistedAtRef = useRef(0);
  const pendingContextSignatureRef = useRef('');
  const inFlightContextSignatureRef = useRef('');
  const contextWriteTimerRef = useRef<number | undefined>(undefined);
  const previewOriginRef = useRef<string | null>(null);
  const activePreviewIdRef = useRef(preview.id);
  const previousPreviewIdRef = useRef(preview.id);
  const browserClientIdRef = useRef<string | null>(preview.browserClientId || null);
  const browserLeaseTokenRef = useRef<string | null>(preview.browserLeaseToken || null);
  const onBrowserContextChangeRef = useRef(onBrowserContextChange);
  onBrowserContextChangeRef.current = onBrowserContextChange;
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const enabled = isGitHubLivePreviewEnabledClient();
  const runtimeReady = ['ready', 'validating', 'invalid'].includes(receipt.status);

  useEffect(() => {
    const previousPreviewId = previousPreviewIdRef.current;
    activePreviewIdRef.current = preview.id;
    previousPreviewIdRef.current = preview.id;
    if (previousPreviewId === preview.id) return;
    if (contextWriteTimerRef.current !== undefined) {
      window.clearTimeout(contextWriteTimerRef.current);
      contextWriteTimerRef.current = undefined;
    }
    contextVersionRef.current = 0;
    contextSignatureRef.current = '';
    contextPersistedAtRef.current = 0;
    pendingContextSignatureRef.current = '';
    inFlightContextSignatureRef.current = '';
    previewOriginRef.current = null;
    browserClientIdRef.current = preview.browserClientId || null;
    browserLeaseTokenRef.current = preview.browserLeaseToken || null;
    setReloadKey(0);
    setReceipt(preview);
    setState('loading');
    setError(null);
    setBrowserContext(null);
    setInspectorEnabled(false);
    setInspectorReady(false);
    setInspectorError(null);
    setReviewOpen(false);
    setReviewBuildStarting(false);
    setReviewBuildError(null);
    setChangesSummary(null);
    setChangesError(null);
    onBrowserContextChangeRef.current?.(null);
  }, [preview]);

  useEffect(() => {
    onPreviewChange?.(receipt);
  }, [onPreviewChange, receipt]);

  const refreshChanges = useCallback(async () => {
    if (!user) return null;
    const requestedPreviewId = preview.id;
    try {
      const token = await user.getIdToken();
      if (activePreviewIdRef.current !== requestedPreviewId) return null;
      const response = await fetch(
        `/api/github-previews/${encodeURIComponent(requestedPreviewId)}/changes`,
        { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
      );
      const payload = (await response.json().catch(() => ({}))) as GitHubPreviewChangesResponse & {
        error?: string;
      };
      if (activePreviewIdRef.current !== requestedPreviewId) return null;
      if (!response.ok || !payload.preview || !Array.isArray(payload.files)) {
        throw new Error(payload.error || 'Changes could not be loaded');
      }
      setChangesSummary(payload);
      setReceipt(current => ({
        ...current,
        status: payload.preview.status,
        revision: payload.preview.revision,
        validatedRevision: payload.preview.validatedRevision,
        validationStatus: payload.preview.validationStatus,
      }));
      setChangesError(null);
      return payload;
    } catch (cause) {
      if (activePreviewIdRef.current !== requestedPreviewId) return null;
      setChangesError(cause instanceof Error ? cause.message : 'Changes could not be loaded');
      return null;
    }
  }, [preview.id, user]);

  useEffect(() => {
    if (!enabled || !user) return;
    void refreshChanges();
  }, [enabled, receipt.revision, receipt.status, refreshChanges, user]);

  useEffect(
    () =>
      subscribeDeliveryApprovalsChanged(projectId => {
        if (projectId === changesSummary?.projectId) void refreshChanges();
      }),
    [changesSummary?.projectId, refreshChanges]
  );

  useEffect(() => {
    setReceipt(current => {
      if (current.id === preview.id && current.revision > preview.revision) return current;
      const next = {
        ...preview,
        browserClientId:
          preview.browserClientId === undefined
            ? current.id === preview.id
              ? current.browserClientId
              : null
            : preview.browserClientId,
        browserLeaseToken:
          preview.browserLeaseToken === undefined
            ? current.id === preview.id
              ? current.browserLeaseToken
              : null
            : preview.browserLeaseToken,
        initialPath:
          preview.initialPath || (current.id === preview.id ? current.initialPath : null),
      };
      return current.id === next.id &&
        current.status === next.status &&
        current.revision === next.revision &&
        current.validatedRevision === next.validatedRevision &&
        current.validationStatus === next.validationStatus &&
        current.previewPort === next.previewPort &&
        current.path === next.path &&
        current.initialPath === next.initialPath &&
        current.repository === next.repository &&
        current.browserClientId === next.browserClientId &&
        current.browserLeaseToken === next.browserLeaseToken &&
        current.error === next.error
        ? current
        : next;
    });
  }, [
    preview.error,
    preview.browserClientId,
    preview.browserLeaseToken,
    preview.id,
    preview.initialPath,
    preview.path,
    preview.previewPort,
    preview.repository,
    preview.revision,
    preview.status,
    preview.validatedRevision,
    preview.validationStatus,
  ]);

  useEffect(() => {
    if (!['failed', 'expired', 'deleting', 'deleted'].includes(receipt.status)) return;
    setState('error');
    setError(receipt.error || 'Preview is no longer available');
  }, [receipt.error, receipt.status]);

  const publishBrowserContext = useCallback(
    async (raw: Omit<GitHubPreviewBrowserContextInput, 'contextVersion'>) => {
      if (!user || !previewOriginRef.current || activePreviewIdRef.current !== preview.id) return;
      const path = sanitizeGitHubPreviewPath(raw.path, previewOriginRef.current);
      if (!path) return;
      let input: GitHubPreviewBrowserContextInput;
      try {
        input = parseGitHubPreviewBrowserContextInput({
          ...raw,
          path,
          contextVersion: Math.max(Date.now(), contextVersionRef.current + 1),
        });
      } catch {
        return;
      }
      const signature = JSON.stringify({
        path: input.path,
        title: input.title,
        viewport: input.viewport,
        selection: input.selection,
      });
      if (signature === pendingContextSignatureRef.current) return;
      const unchangedContext =
        signature === contextSignatureRef.current &&
        (!inFlightContextSignatureRef.current || inFlightContextSignatureRef.current === signature);
      if (unchangedContext) {
        if (
          pendingContextSignatureRef.current &&
          pendingContextSignatureRef.current !== signature
        ) {
          return;
        }
        if (Date.now() - contextPersistedAtRef.current < BROWSER_CONTEXT_HEARTBEAT_MS) return;
      }
      if (contextWriteTimerRef.current !== undefined) {
        window.clearTimeout(contextWriteTimerRef.current);
        contextWriteTimerRef.current = undefined;
        pendingContextSignatureRef.current = '';
      }
      pendingContextSignatureRef.current = signature;
      contextVersionRef.current = input.contextVersion;
      const optimisticContext: GitHubPreviewBrowserContextSnapshot = {
        ...input,
        previewId: preview.id,
        capturedAt: new Date().toISOString(),
      };
      setBrowserContext(optimisticContext);
      onBrowserContextChangeRef.current?.(optimisticContext);
      contextWriteTimerRef.current = window.setTimeout(
        () => {
          contextWriteTimerRef.current = undefined;
          inFlightContextSignatureRef.current = signature;
          void (async () => {
            try {
              const token = await user.getIdToken();
              const response = await fetch(
                `/api/github-previews/${encodeURIComponent(preview.id)}/browser-context`,
                {
                  method: 'PUT',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(input),
                }
              );
              const payload = (await response.json().catch(() => ({}))) as {
                context?: GitHubPreviewBrowserContextSnapshot | null;
              };
              if (activePreviewIdRef.current !== preview.id) return;
              if (!payload.context || (![200, 409].includes(response.status) && !response.ok)) {
                if (pendingContextSignatureRef.current === signature)
                  pendingContextSignatureRef.current = '';
                if (inFlightContextSignatureRef.current === signature)
                  inFlightContextSignatureRef.current = '';
                return;
              }
              const persistedSignature = JSON.stringify({
                path: payload.context.path,
                title: payload.context.title,
                viewport: payload.context.viewport,
                selection: payload.context.selection,
              });
              if (pendingContextSignatureRef.current === signature)
                pendingContextSignatureRef.current = '';
              if (inFlightContextSignatureRef.current === signature)
                inFlightContextSignatureRef.current = '';
              const isLatestPersistedContext =
                payload.context.contextVersion >= contextVersionRef.current;
              const isEquivalentPersistedContext = persistedSignature === signature;
              contextVersionRef.current = Math.max(
                contextVersionRef.current,
                payload.context.contextVersion
              );
              if (isLatestPersistedContext || isEquivalentPersistedContext) {
                contextSignatureRef.current = persistedSignature;
                contextPersistedAtRef.current = Date.now();
              }
              if (isLatestPersistedContext) {
                setBrowserContext(payload.context);
                onBrowserContextChangeRef.current?.(payload.context);
              }
            } catch {
              if (pendingContextSignatureRef.current === signature)
                pendingContextSignatureRef.current = '';
              if (inFlightContextSignatureRef.current === signature)
                inFlightContextSignatureRef.current = '';
              // Preview rendering remains available while context persistence is temporarily unavailable.
            }
          })();
        },
        raw.selection ? 0 : 600
      );
    },
    [preview.id, user]
  );

  const clearInspectedSelection = useCallback(() => {
    setInspectorEnabled(false);
    previewApiRef.current?.sendMessage({ type: 'ATTUNE_SELECTION_CLEAR' });
  }, []);

  const annotation = usePreviewAnnotation({
    target: {
      kind: 'website',
      previewId: preview.id,
      revision: receipt.revision,
      path: browserContext?.path || '/',
    },
    enabled: enabled && runtimeReady,
    resetKey: `${reloadKey}:${receipt.initialPath || ''}:${state === 'error'}`,
    sendCapture: message => {
      if (!previewApiRef.current) throw new Error('Preview is reconnecting. Try again.');
      previewApiRef.current.sendMessage(message);
    },
    cancelCapture: requestId => {
      previewApiRef.current?.sendMessage({
        source: 'attune-host',
        type: 'annotation-cancel',
        requestId,
      });
    },
    beforeCapture: () => {
      clearInspectedSelection();
      previewApiRef.current?.sendMessage({ type: 'ATTUNE_INSPECTOR_SET', enabled: false });
      previewApiRef.current?.sendMessage({ type: 'ATTUNE_CONTEXT_CAPTURE' });
    },
  });
  const { reset: resetAnnotation, receive: receiveAnnotation } = annotation;

  useAgentPreviewCapture({
    enabled: enabled && runtimeReady && state === 'ready' && !reviewOpen,
    // The server checks revisions; this panel receipt can lag a same-turn edit.
    matches: request =>
      request.target.kind === 'website' && request.target.previewId === preview.id,
    capture: async (request, signal) => {
      const frame = previewApiRef.current?.iframe;
      if (!frame || request.target.kind !== 'website')
        return { status: 'unavailable', error: 'The website preview is not open.' };
      const url = new URL(frame.src);
      const route = new URL(request.target.path, url.origin);
      url.pathname = route.pathname;
      const previewToken = url.searchParams.get('preview_token');
      url.search = route.search;
      if (previewToken) url.searchParams.set('preview_token', previewToken);
      url.hash = route.hash;
      const { captureWebsiteFrame } = await import('@/components/preview/capture-frames');
      const viewport =
        browserContext?.path === request.target.path ? browserContext.viewport : null;
      return captureWebsiteFrame({
        request,
        signal,
        url: url.toString(),
        width: frame.clientWidth,
        height: frame.clientHeight,
        scroll: { x: viewport?.scrollX || 0, y: viewport?.scrollY || 0 },
      });
    },
  });

  useEffect(() => {
    onSelectionClearHandlerChange?.(clearInspectedSelection);
    return () => onSelectionClearHandlerChange?.(null);
  }, [clearInspectedSelection, onSelectionClearHandlerChange]);

  useEffect(() => {
    const syncVisibility = () => {
      const visible = document.visibilityState === 'visible';
      setPageVisible(visible);
    };
    syncVisibility();
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, []);

  const updateReceipt = useCallback((raw: Record<string, unknown> | undefined) => {
    if (!raw) return;
    setReceipt(current => ({
      ...current,
      status: typeof raw.status === 'string' ? raw.status : current.status,
      revision:
        typeof raw.revision === 'number'
          ? raw.revision
          : typeof raw.current_revision === 'number'
            ? raw.current_revision
            : current.revision,
      validatedRevision:
        typeof raw.validatedRevision === 'number'
          ? raw.validatedRevision
          : typeof raw.last_validated_revision === 'number'
            ? raw.last_validated_revision
            : current.validatedRevision,
      validationStatus:
        typeof raw.validationStatus === 'string'
          ? raw.validationStatus
          : typeof raw.last_validation_status === 'string'
            ? raw.last_validation_status
            : current.validationStatus,
      error:
        typeof raw.error === 'string'
          ? raw.error
          : typeof raw.last_error === 'string'
            ? raw.last_error
            : null,
      repository: typeof raw.repository === 'string' ? raw.repository : current.repository,
      path: typeof raw.path === 'string' ? raw.path : current.path,
      previewPort:
        typeof raw.previewPort === 'number'
          ? raw.previewPort
          : typeof raw.preview_port === 'number'
            ? raw.preview_port
            : current.previewPort,
      initialPath: previewInitialPath(raw.initialPath) || current.initialPath,
    }));
  }, []);

  const toggleReview = useCallback(async () => {
    if (reviewOpen) {
      setReviewOpen(false);
      return;
    }
    setReviewOpen(true);
    setReviewBuildError(null);
    if (!user || receipt.revision < 1 || receipt.validationStatus !== 'pending') return;
    setReviewBuildStarting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `/api/github-previews/${encodeURIComponent(preview.id)}/session`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'validate', revision: receipt.revision }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        preview?: Record<string, unknown>;
        code?: string;
        error?: string;
      };
      if (!response.ok || !payload.preview) {
        if (response.status === 409 && payload.code === 'GITHUB_PREVIEW_STALE') {
          await refreshChanges();
          throw new Error(
            'The site changed while Review was opening. Return to Preview, then open Review & submit again to build the latest revision.'
          );
        }
        throw new Error(payload.error || 'The preview build could not be started');
      }
      if (activePreviewIdRef.current !== preview.id) return;
      updateReceipt(payload.preview);
      await refreshChanges();
    } catch (cause) {
      if (activePreviewIdRef.current === preview.id) {
        setReviewBuildError(
          cause instanceof Error ? cause.message : 'The preview build could not be started'
        );
      }
    } finally {
      if (activePreviewIdRef.current === preview.id) setReviewBuildStarting(false);
    }
  }, [
    preview.id,
    receipt.revision,
    receipt.validationStatus,
    refreshChanges,
    reviewOpen,
    updateReceipt,
    user,
  ]);

  const releaseBrowserLease = useCallback(
    async (previewId: string, browserClientId: string, browserLeaseToken: string) => {
      if (!user) return;
      try {
        const token = await user.getIdToken();
        await fetch(`/api/github-previews/${encodeURIComponent(previewId)}/session`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'hibernate',
            browserClientId,
            browserLeaseToken,
          }),
          keepalive: true,
        });
      } catch {
        // The lease expires quickly if the browser cannot deliver the close request.
      }
    },
    [user]
  );

  const getPreviewAccess = useCallback(
    async (isActive: () => boolean) => {
      if (!user) throw new Error('Sign in to view this preview');
      const requestedPreviewId = preview.id;
      const browserClientId = browserClientIdRef.current || window.crypto.randomUUID();
      browserClientIdRef.current = browserClientId;
      const token = await user.getIdToken();
      if (!isActive() || activePreviewIdRef.current !== requestedPreviewId)
        throw new Error('Preview changed');
      const response = await fetch(
        `/api/github-previews/${encodeURIComponent(requestedPreviewId)}/session?browserClientId=${encodeURIComponent(browserClientId)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }
      );
      const payload = (await response.json()) as {
        access?: { url?: string; expiresAt?: string };
        preview?: Record<string, unknown>;
        browserLeaseToken?: string;
        error?: string;
      };
      if (!response.ok || !payload.access?.url)
        throw new Error(payload.error || 'Preview is unavailable');
      if (!payload.browserLeaseToken) throw new Error('Preview browser lease is unavailable');
      if (!isActive() || activePreviewIdRef.current !== requestedPreviewId) {
        void releaseBrowserLease(requestedPreviewId, browserClientId, payload.browserLeaseToken);
        throw new Error('Preview changed');
      }
      browserLeaseTokenRef.current = payload.browserLeaseToken;
      updateReceipt(payload.preview);
      setReceipt(current => ({
        ...current,
        browserClientId,
        browserLeaseToken: payload.browserLeaseToken,
      }));
      const previewPort =
        typeof payload.preview?.previewPort === 'number'
          ? payload.preview.previewPort
          : typeof payload.preview?.preview_port === 'number'
            ? payload.preview.preview_port
            : receipt.previewPort;
      return {
        url: payload.access.url,
        expiresAt: payload.access.expiresAt || null,
        previewPort,
      };
    },
    [preview.id, receipt.previewPort, releaseBrowserLease, updateReceipt, user]
  );

  const closePreview = useCallback(() => {
    const previewId = activePreviewIdRef.current;
    const browserClientId = browserClientIdRef.current;
    const browserLeaseToken = browserLeaseTokenRef.current;
    activePreviewIdRef.current = '';
    browserLeaseTokenRef.current = null;
    if (previewId && browserClientId && browserLeaseToken) {
      void releaseBrowserLease(previewId, browserClientId, browserLeaseToken);
    }
    onClose?.();
  }, [onClose, releaseBrowserLease]);

  useEffect(() => {
    if (
      !enabled ||
      !user ||
      !pageVisible ||
      ![
        'queued',
        'provisioning',
        'installing',
        'ready',
        'validating',
        'invalid',
        'hibernated',
      ].includes(receipt.status)
    )
      return;
    let cancelled = false;
    const requestedPreviewId = preview.id;
    let pollTimer: number | undefined;
    let requestController: AbortController | undefined;
    const scheduleRefresh = () => {
      if (!cancelled && activePreviewIdRef.current === requestedPreviewId)
        pollTimer = window.setTimeout(
          () => void refresh(),
          ['ready', 'invalid'].includes(receipt.status) ? 30_000 : 3000
        );
    };
    const refresh = async () => {
      try {
        const token = await user.getIdToken();
        if (cancelled || activePreviewIdRef.current !== requestedPreviewId) return;
        requestController = new AbortController();
        const browserClientId = browserClientIdRef.current;
        const browserLeaseToken = browserLeaseTokenRef.current;
        const leaseQuery =
          browserClientId && browserLeaseToken
            ? `&browserClientId=${encodeURIComponent(browserClientId)}&browserLeaseToken=${encodeURIComponent(browserLeaseToken)}`
            : '';
        const response = await fetch(
          `/api/github-previews/${encodeURIComponent(requestedPreviewId)}/session?status=1${leaseQuery}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
            signal: requestController.signal,
          }
        );
        const payload = (await response.json().catch(() => ({}))) as {
          preview?: Record<string, unknown>;
          error?: string;
          code?: string;
        };
        if (cancelled || activePreviewIdRef.current !== requestedPreviewId) return;
        if (response.ok) {
          updateReceipt(payload.preview);
          const previewStatus =
            typeof payload.preview?.status === 'string' ? payload.preview.status : null;
          if (previewStatus && ['failed', 'expired', 'deleted'].includes(previewStatus)) {
            const message =
              (typeof payload.preview?.error === 'string' && payload.preview.error) ||
              'Preview is no longer available';
            setState('error');
            setError(message);
          }
        } else if (
          response.status === 409 &&
          payload.code === 'GITHUB_PREVIEW_BROWSER_LEASE_EXPIRED'
        ) {
          if (browserLeaseTokenRef.current !== browserLeaseToken) return;
          browserLeaseTokenRef.current = null;
          setReceipt(current => ({ ...current, browserLeaseToken: null }));
          setReloadKey(key => key + 1);
        } else if ([403, 404, 410].includes(response.status)) {
          const message = payload.error || 'Preview is no longer available';
          setReceipt(current => ({ ...current, status: 'expired', error: message }));
          setState('error');
          setError(message);
        }
      } catch {
        // The embedded preview retains its last receipt while a status poll is unavailable.
      } finally {
        requestController = undefined;
        scheduleRefresh();
      }
    };
    void refresh();
    return () => {
      cancelled = true;
      requestController?.abort();
      if (pollTimer !== undefined) window.clearTimeout(pollTimer);
    };
  }, [enabled, pageVisible, preview.id, receipt.status, updateReceipt, user]);

  useEffect(() => {
    if (!user || !enabled) return;
    let cancelled = false;
    const requestedPreviewId = preview.id;
    void (async () => {
      try {
        const token = await user.getIdToken();
        if (cancelled || activePreviewIdRef.current !== requestedPreviewId) return;
        const response = await fetch(
          `/api/github-previews/${encodeURIComponent(requestedPreviewId)}/browser-context`,
          { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
        );
        const payload = (await response.json().catch(() => ({}))) as {
          context?: GitHubPreviewBrowserContextSnapshot | null;
        };
        if (
          cancelled ||
          activePreviewIdRef.current !== requestedPreviewId ||
          !response.ok ||
          !payload.context
        )
          return;
        contextVersionRef.current = Math.max(
          contextVersionRef.current,
          payload.context.contextVersion
        );
        contextSignatureRef.current = JSON.stringify({
          path: payload.context.path,
          title: payload.context.title,
          viewport: payload.context.viewport,
          selection: payload.context.selection,
        });
        contextPersistedAtRef.current = Date.parse(payload.context.capturedAt) || Date.now();
        setBrowserContext(payload.context);
        onBrowserContextChangeRef.current?.(payload.context);
      } catch {
        // A fresh context is captured after the iframe connects.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, preview.id, user]);

  useEffect(() => {
    if (!enabled || !user || !containerRef.current || !runtimeReady) return;
    let cancelled = false;
    const requestedPreviewId = preview.id;
    const isActivePreview = () => !cancelled && activePreviewIdRef.current === requestedPreviewId;
    let dispose: (() => void) | undefined;
    containerRef.current.replaceChildren();
    setState('loading');
    setError(null);
    setInspectorReady(false);
    setInspectorError(null);
    void (async () => {
      try {
        const access = await getPreviewAccess(isActivePreview);
        if (!isActivePreview()) return;
        const accessUrl = new URL(access.url);
        const previewUrl = new URL(accessUrl);
        if (receipt.initialPath) {
          const requestedUrl = new URL(receipt.initialPath, accessUrl.origin);
          previewUrl.pathname = requestedUrl.pathname;
          const accessQueryKeys = new Set(previewUrl.searchParams.keys());
          requestedUrl.searchParams.forEach((value, key) => {
            if (!accessQueryKeys.has(key)) previewUrl.searchParams.append(key, value);
          });
          previewUrl.hash = requestedUrl.hash;
        }
        const embeddedSourceUrl = previewUrl.toString();
        previewOriginRef.current = accessUrl.origin;
        const embedded = createPreview<AttunePreviewMessageToFrame, AttunePreviewMessageFromFrame>(
          embeddedSourceUrl
        );
        const iframe = embedded.iframe;
        previewApiRef.current = embedded;
        const installBridge = () => {
          if (!isActivePreview()) return;
          void embedded.injectAndInvoke(installAttunePreviewAnnotationBridge, {});
          void embedded.injectAndInvoke(installAttunePreviewContextBridge, {
            inspectorEnabled: inspectorEnabledRef.current,
            selectionColor: PREVIEW_INSPECTOR_COLOR,
          });
        };
        const bridgeLifecycle = createPreviewBridgeLifecycle({
          install: installBridge,
          onUnavailable: () => {
            if (!isActivePreview()) return;
            setInspectorReady(false);
            setInspectorError('Inspector could not connect. Refresh the preview to retry.');
          },
        });
        iframe.title = `Live preview of ${receipt.repository}`;
        iframe.referrerPolicy = 'no-referrer';
        iframe.style.width = '100%';
        iframe.style.height = variant === 'rail' ? '100%' : '640px';
        iframe.style.border = '0';
        const messageDispose = embedded.onMessage(message => {
          if (!isActivePreview()) return;
          if (message.type === 'annotation-result') {
            receiveAnnotation(message);
            return;
          }
          if (message.type === 'SET_URL') {
            const path = sanitizeGitHubPreviewPath(message.url, accessUrl.origin);
            if (path && browserContextPathRef.current && path !== browserContextPathRef.current) {
              resetAnnotation();
              clearInspectedSelection();
            }
            embedded.sendMessage({ type: 'ATTUNE_CONTEXT_CAPTURE' });
            return;
          }
          if (message.type === 'ATTUNE_PREVIEW_CONTEXT') {
            const path = sanitizeGitHubPreviewPath(message.context.path, accessUrl.origin);
            if (path && browserContextPathRef.current && path !== browserContextPathRef.current)
              resetAnnotation();
            void publishBrowserContext(message.context);
            return;
          }
          if (message.type === 'ATTUNE_INSPECTOR_STATE') {
            bridgeLifecycle.acknowledged();
            setInspectorReady(true);
            setInspectorError(null);
            setInspectorEnabled(message.enabled);
          }
        });
        const statusDispose = embedded.onStatusChange(status => {
          if (!isActivePreview()) return;
          if (status === 'DISCONNECTED') {
            resetAnnotation();
            // The Browser Preview API emits DISCONNECTED while the embedded
            // document navigates or reloads. The same Preview instance then
            // reconnects on iframe load, so replacing it here creates a reload
            // loop and prevents the inspector bridge from settling.
            setInspectorReady(false);
            bridgeLifecycle.disconnected();
            return;
          }
          if (status !== 'CONNECTED') return;
          setState('ready');
          setInspectorError(null);
          bridgeLifecycle.connected();
        });
        // The SDK's iframe load listener marks the Preview CONNECTED before
        // this listener runs. Re-requesting installation here covers document
        // reloads where the status event is coalesced or observed late.
        const frameLoaded = () => {
          resetAnnotation();
          installBridge();
        };
        iframe.addEventListener('load', frameLoaded);
        iframe.addEventListener('error', () => {
          if (isActivePreview()) {
            setState('error');
            setError('Preview failed to load');
          }
        });
        containerRef.current?.replaceChildren(iframe);
        const parsedExpiry = access.expiresAt ? Date.parse(access.expiresAt) : Number.NaN;
        const refreshDelay = Number.isFinite(parsedExpiry)
          ? Math.max(60_000, parsedExpiry - Date.now() - 60_000)
          : 14 * 60_000;
        const refreshTimer = window.setTimeout(() => setReloadKey(key => key + 1), refreshDelay);
        dispose = () => {
          window.clearTimeout(refreshTimer);
          bridgeLifecycle.dispose();
          messageDispose();
          statusDispose.dispose();
          iframe.removeEventListener('load', frameLoaded);
          if (previewApiRef.current === embedded) previewApiRef.current = null;
          embedded.dispose();
        };
      } catch (cause) {
        if (isActivePreview()) {
          setState('error');
          setError(cause instanceof Error ? cause.message : 'Preview failed to load');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (contextWriteTimerRef.current !== undefined) {
        window.clearTimeout(contextWriteTimerRef.current);
        contextWriteTimerRef.current = undefined;
      }
      pendingContextSignatureRef.current = '';
      inFlightContextSignatureRef.current = '';
      dispose?.();
      containerRef.current?.replaceChildren();
    };
  }, [
    enabled,
    getPreviewAccess,
    clearInspectedSelection,
    publishBrowserContext,
    preview.id,
    receipt.initialPath,
    receipt.repository,
    reloadKey,
    runtimeReady,
    receiveAnnotation,
    resetAnnotation,
    user,
    variant,
  ]);

  if (!enabled) return null;
  const toggleInspector = () => {
    if (!inspectorReady) return;
    annotation.pause();
    const enabled = !inspectorEnabled;
    setInspectorEnabled(enabled);
    previewApiRef.current?.sendMessage({ type: 'ATTUNE_INSPECTOR_SET', enabled });
  };
  const contextLabel = browserContext?.selection
    ? browserContext.selection.label ||
      browserContext.selection.text ||
      browserContext.selection.tag
    : browserContext?.viewport.visibleHeadings[0]?.text || null;
  const exactBuildPassed =
    receipt.validationStatus === 'passed' && receipt.validatedRevision === receipt.revision;
  const validationTone = exactBuildPassed
    ? 'green'
    : receipt.validationStatus === 'failed'
      ? 'red'
      : 'gray';
  const validationLabel = exactBuildPassed
    ? 'Build passed'
    : receipt.validationStatus === 'failed'
      ? 'Build failed'
      : receipt.status === 'validating'
        ? 'Building'
        : 'Ready for review';
  return (
    <Box
      mt={variant === 'rail' ? '0' : '4'}
      h={variant === 'rail' ? 'full' : 'auto'}
      minH={0}
      display="flex"
      flexDirection="column"
      borderWidth={variant === 'rail' ? '0' : '1px'}
      borderColor="ui.border.muted"
      borderRadius={variant === 'rail' ? '0' : 'xl'}
      overflow="hidden"
    >
      {variant === 'rail' ? (
        <HStack
          minH="54px"
          px="3"
          borderBottomWidth="1px"
          borderColor="ui.border.subtle"
          bg="ui.surface.rail"
          justify="space-between"
          flexShrink={0}
        >
          <HStack gap="2" minW={0}>
            <Monitor size={17} aria-hidden="true" />
            <Box minW={0}>
              <Text fontSize="13px" fontWeight="550" truncate>
                Website preview
              </Text>
              {browserContext ? (
                <Text fontSize="10px" color="ui.text.tertiary" truncate maxW="280px">
                  {browserContext.path}
                  {contextLabel ? ` · ${contextLabel}` : ''}
                </Text>
              ) : null}
            </Box>
          </HStack>
          <HStack gap="0.5">
            <PreviewInspectorButton
              enabled={inspectorEnabled}
              ready={state === 'ready' && inspectorReady}
              error={inspectorError}
              onToggle={toggleInspector}
            />
            {onBrowserContextChange ? (
              <PreviewAnnotationToolbar
                annotation={annotation}
                disabled={state !== 'ready' || !inspectorReady || !browserContext}
              />
            ) : null}
            <IconButton
              aria-label="Refresh website preview"
              title="Refresh website preview"
              size="sm"
              variant="ghost"
              display={{ base: 'none', md: 'inline-flex' }}
              onClick={() => setReloadKey(key => key + 1)}
            >
              <RefreshCw size={15} aria-hidden="true" />
            </IconButton>
            <IconButton
              asChild
              aria-label="Open website preview in a new tab"
              title="Open website preview in a new tab"
              size="sm"
              variant="ghost"
              display={{ base: 'none', md: 'inline-flex' }}
            >
              <a href={websitePreviewHref(receipt)} target="_blank" rel="noreferrer">
                <ExternalLink size={15} aria-hidden="true" />
              </a>
            </IconButton>
            <Box mx="1.5" h="5" w="1px" bg="ui.border.subtle" aria-hidden="true" />
            <Tooltip content={`${validationLabel} for revision ${receipt.revision}`}>
              <Box
                as="span"
                role="status"
                aria-label={validationLabel}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="8"
                h="8"
                color={
                  exactBuildPassed
                    ? 'status.success.fg'
                    : receipt.validationStatus === 'failed'
                      ? 'status.danger.fg'
                      : 'ui.text.tertiary'
                }
              >
                {exactBuildPassed ? (
                  <CheckCircle2 size={17} aria-hidden="true" />
                ) : receipt.validationStatus === 'failed' ? (
                  <CircleX size={17} aria-hidden="true" />
                ) : receipt.status === 'validating' ? (
                  <Spinner size="xs" />
                ) : (
                  <Clock3 size={17} aria-hidden="true" />
                )}
              </Box>
            </Tooltip>
            <Button
              size="xs"
              variant={reviewOpen ? 'solid' : 'outline'}
              bg={reviewOpen ? undefined : 'ui.surface.card'}
              onClick={() => void toggleReview()}
              disabled={reviewBuildStarting}
              aria-label={`Review and submit ${changesSummary?.summary.files ?? 0} website updates`}
              aria-pressed={reviewOpen}
              title="Review and submit all current website updates"
            >
              {reviewBuildStarting ? (
                <Spinner size="xs" />
              ) : (
                <FileDiff size={14} aria-hidden="true" />
              )}
              <Box as="span" display={{ base: 'none', md: 'inline' }}>
                Review &amp; submit ·
              </Box>
              {changesSummary?.summary.files ?? 0}
            </Button>
            {onClose ? (
              <>
                <Box mx="1" h="5" w="1px" bg="ui.border.subtle" aria-hidden="true" />
                <SidePanelToggleButton open label="Close website preview" onClick={closePreview} />
              </>
            ) : null}
          </HStack>
        </HStack>
      ) : (
        <Flex px="3" py="2.5" align="center" justify="space-between" gap="3" bg="ui.surface.subtle">
          <HStack gap="2" minW={0}>
            <Monitor size={16} />
            <Text fontSize="sm" fontWeight="semibold" truncate>
              {receipt.repository}
            </Text>
            <Badge colorPalette={validationTone} size="sm">
              Revision {receipt.revision} · {validationLabel}
            </Badge>
          </HStack>
          <HStack gap="1">
            {onBrowserContextChange ? (
              <PreviewAnnotationToolbar
                annotation={annotation}
                disabled={state !== 'ready' || !inspectorReady || !browserContext}
              />
            ) : null}
            <Button
              size="xs"
              variant={inspectorEnabled ? 'solid' : 'ghost'}
              colorPalette={inspectorEnabled ? 'blue' : undefined}
              onClick={toggleInspector}
              disabled={state !== 'ready' || !inspectorReady}
              title={inspectorError || (!inspectorReady ? 'Inspector is connecting' : undefined)}
              aria-pressed={inspectorEnabled}
            >
              <Crosshair size={14} /> {inspectorEnabled ? 'Inspecting' : 'Inspect'}
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setReloadKey(key => key + 1)}>
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button size="xs" variant="ghost" asChild>
              <a href={websitePreviewHref(receipt)} target="_blank" rel="noreferrer">
                <ExternalLink size={14} /> Open
              </a>
            </Button>
            <Box mx="1" h="5" w="1px" bg="ui.border.subtle" aria-hidden="true" />
            <Button
              size="xs"
              variant={reviewOpen ? 'solid' : 'outline'}
              onClick={() => void toggleReview()}
              disabled={reviewBuildStarting}
              aria-pressed={reviewOpen}
            >
              {reviewBuildStarting ? <Spinner size="xs" /> : <FileDiff size={14} />}
              Review &amp; submit · {changesSummary?.summary.files ?? 0}
            </Button>
          </HStack>
        </Flex>
      )}
      {reviewOpen && reviewBuildError ? (
        <Text px="4" py="2" fontSize="sm" color="status.danger.fg">
          {reviewBuildError}
        </Text>
      ) : null}
      {reviewOpen && changesSummary ? (
        <GitHubPreviewChangesPanel
          previewId={preview.id}
          summary={changesSummary}
          onSummaryChange={setChangesSummary}
          onSummaryRefresh={refreshChanges}
          onBack={() => setReviewOpen(false)}
        />
      ) : null}
      {reviewOpen && !changesSummary ? (
        <Flex flex="1" minH="240px" direction="column" gap="3" align="center" justify="center">
          {changesError ? (
            <>
              <Text fontSize="sm" color="ui.text.secondary">
                {changesError}
              </Text>
              <Button size="xs" variant="outline" onClick={() => void refreshChanges()}>
                Retry
              </Button>
            </>
          ) : (
            <Spinner size="sm" />
          )}
        </Flex>
      ) : null}
      {!reviewOpen && state === 'loading' ? (
        <PreviewLoadingState status={receipt.status} variant={variant} />
      ) : null}
      {!reviewOpen && state === 'error' ? (
        <Text p="4" fontSize="sm" color="red.600">
          {error}
        </Text>
      ) : null}
      <Box
        display={!reviewOpen && state === 'ready' ? 'block' : 'none'}
        flex={variant === 'rail' ? '1' : undefined}
        minH={variant === 'rail' ? '0' : undefined}
        h={variant === 'rail' ? undefined : '640px'}
        position="relative"
        bg="white"
      >
        <Box ref={containerRef} h="full" pointerEvents={annotation.active ? 'none' : 'auto'} />
        <PreviewAnnotationOverlay annotation={annotation} />
      </Box>
    </Box>
  );
}
